import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { IMessage } from "@stomp/stompjs";
import { getConstants } from "@utilifiles/methods/storage/storage";
import {
  createConversation,
  getMessageHistory,
  listConversations,
  markDeliveredRest,
  markReadRest,
  sendMessageRest,
} from "./services";
import {
  createMessengerStomp,
  type MessengerStompHandle,
} from "./stompClient";
import type {
  ChatMessageResponse,
  ConversationDto,
  ReceiptEventDto,
  ReceiptStatus,
  UiChatMessage,
} from "./types";

type MessengerContextValue = {
  conversations: ConversationDto[];
  conversationsLoading: boolean;
  activeConversationId: number | null;
  activeConversation: ConversationDto | null;
  messages: UiChatMessage[];
  messagesLoading: boolean;
  connected: boolean;
  totalUnread: number;
  mobileShowThread: boolean;
  setMobileShowThread: (v: boolean) => void;
  setDrawerViewState: (state: {
    drawerOpen: boolean;
    threadVisible: boolean;
  }) => void;
  refreshConversations: () => Promise<void>;
  openConversation: (conversationId: number) => Promise<void>;
  startChatWithUser: (receiverId: number) => Promise<ConversationDto | null>;
  sendMessage: (content: string) => Promise<void>;
  clearActiveConversation: () => void;
};

const MessengerContext = createContext<MessengerContextValue | null>(null);
const RECEIPT_CACHE_KEY = "messenger_receipt_status_map";

function parseBody<T>(frame: IMessage): T | null {
  try {
    return JSON.parse(frame.body) as T;
  } catch {
    return null;
  }
}

function sortConversations(list: ConversationDto[]): ConversationDto[] {
  return [...list].sort((a, b) => {
    const aTime = a.lastMessageAt || a.updatedAt || "";
    const bTime = b.lastMessageAt || b.updatedAt || "";
    return bTime.localeCompare(aTime);
  });
}

function statusRank(status?: UiChatMessage["deliveryStatus"]): number {
  if (status === "READ") return 3;
  if (status === "DELIVERED") return 2;
  if (status === "SENT") return 1;
  return 0;
}

function mergeStatus(
  current?: UiChatMessage["deliveryStatus"],
  incoming?: UiChatMessage["deliveryStatus"],
): UiChatMessage["deliveryStatus"] | undefined {
  return statusRank(incoming) >= statusRank(current) ? incoming : current;
}

export function MessengerProvider({ children }: { children: ReactNode }) {
  const { userId } = getConstants();
  const currentUserId = Number(userId || 0);

  const [conversations, setConversations] = useState<ConversationDto[]>([]);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState<number | null>(
    null,
  );
  const [messages, setMessages] = useState<UiChatMessage[]>([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [mobileShowThread, setMobileShowThread] = useState(false);

  const stompRef = useRef<MessengerStompHandle | null>(null);
  const activeIdRef = useRef<number | null>(null);
  const currentUserIdRef = useRef(currentUserId);
  const viewStateRef = useRef({ drawerOpen: false, threadVisible: false });
  const onMessageRef = useRef<(frame: IMessage) => void>(() => undefined);
  const onReceiptRef = useRef<(frame: IMessage) => void>(() => undefined);
  const receiptStatusByMessageIdRef = useRef<
    Map<number, ReceiptStatus>
  >(new Map());

  const persistReceiptCache = useCallback(() => {
    try {
      const payload = Array.from(receiptStatusByMessageIdRef.current.entries());
      window.localStorage.setItem(RECEIPT_CACHE_KEY, JSON.stringify(payload));
    } catch {
      /* ignore */
    }
  }, []);

  const setReceiptStatus = useCallback(
    (messageId: number, status: ReceiptStatus) => {
      const current = receiptStatusByMessageIdRef.current.get(messageId);
      const next = mergeStatus(current, status);
      if (!next || (next !== "DELIVERED" && next !== "READ")) return;
      receiptStatusByMessageIdRef.current.set(messageId, next);
      persistReceiptCache();
    },
    [persistReceiptCache],
  );

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(RECEIPT_CACHE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Array<[number, ReceiptStatus]>;
      const normalized = parsed
        .filter((entry) => Array.isArray(entry) && entry.length === 2)
        .map(([id, status]) => [Number(id), status] as [number, ReceiptStatus])
        .filter(
          ([id, status]) =>
            Number.isFinite(id) && (status === "DELIVERED" || status === "READ"),
        );
      receiptStatusByMessageIdRef.current = new Map(normalized);
    } catch {
      receiptStatusByMessageIdRef.current = new Map();
    }
  }, []);

  useEffect(() => {
    activeIdRef.current = activeConversationId;
  }, [activeConversationId]);

  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);

  const setDrawerViewState = useCallback(
    (state: { drawerOpen: boolean; threadVisible: boolean }) => {
      viewStateRef.current = state;
    },
    [],
  );

  const isViewingConversation = useCallback((conversationId: number) => {
    const { drawerOpen, threadVisible } = viewStateRef.current;
    return (
      drawerOpen &&
      threadVisible &&
      activeIdRef.current === conversationId
    );
  }, []);

  const refreshConversations = useCallback(async () => {
    setConversationsLoading(true);
    try {
      const result = await listConversations();
      if (result.responseIndicator === "success" && result.response) {
        setConversations(sortConversations(result.response));
      }
    } catch (e) {
      console.error("Failed to load conversations", e);
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  const upsertConversationPreview = useCallback(
    (msg: ChatMessageResponse, bumpUnread: boolean) => {
      setConversations((prev) => {
        const idx = prev.findIndex((c) => c.id === msg.conversationId);
        if (idx === -1) {
          void refreshConversations();
          return prev;
        }
        const next = [...prev];
        const existing = next[idx];
        next[idx] = {
          ...existing,
          lastMessage: msg.message,
          lastMessageAt: msg.createdAt,
          updatedAt: msg.createdAt,
          unreadCount: bumpUnread
            ? (existing.unreadCount || 0) + 1
            : existing.unreadCount || 0,
        };
        return sortConversations(next);
      });
    },
    [refreshConversations],
  );

  const ackDelivered = useCallback((messageId: number) => {
    const stomp = stompRef.current;
    if (stomp?.isConnected()) {
      stomp.publishDelivered(messageId);
    } else {
      void markDeliveredRest(messageId);
    }
  }, []);

  const markConversationRead = useCallback(
    (conversationId: number, lastReadMessageId?: number) => {
      const stomp = stompRef.current;
      if (stomp?.isConnected()) {
        stomp.publishRead(conversationId, lastReadMessageId);
      } else {
        void markReadRest(conversationId, lastReadMessageId);
      }
      setConversations((prev) =>
        prev.map((c) =>
          c.id === conversationId ? { ...c, unreadCount: 0 } : c,
        ),
      );
    },
    [],
  );

  const handleIncomingMessage = useCallback(
    (frame: IMessage) => {
      const msg = parseBody<ChatMessageResponse>(frame);
      if (!msg?.messageId) return;

      const isViewing = isViewingConversation(msg.conversationId);
      const isOwn = msg.senderId === currentUserIdRef.current;

      if (isViewing) {
        setMessages((prev) => {
          if (prev.some((m) => m.messageId === msg.messageId)) return prev;
          return [
            ...prev,
            {
              ...msg,
              deliveryStatus: isOwn ? "SENT" : undefined,
            },
          ];
        });
        if (!isOwn) {
          markConversationRead(msg.conversationId, msg.messageId);
        }
      }

      upsertConversationPreview(msg, !isViewing && !isOwn);

      if (!isOwn) {
        ackDelivered(msg.messageId);
      }
    },
    [
      ackDelivered,
      isViewingConversation,
      markConversationRead,
      upsertConversationPreview,
    ],
  );

  const handleReceipt = useCallback((frame: IMessage) => {
    const receipt = parseBody<ReceiptEventDto>(frame);
    if (!receipt?.messageId) return;
    const receiptMessageId = Number(receipt.messageId);
    const receiptConversationId = Number(receipt.conversationId);
    setReceiptStatus(receiptMessageId, receipt.status);
    setMessages((prev) =>
      prev.map((m) => {
        if (
          Number(m.messageId) !== receiptMessageId ||
          Number(m.conversationId) !== receiptConversationId
        ) {
          return m;
        }
        const nextStatus = receipt.status;
        const current = m.deliveryStatus;
        if (current === "READ") return m;
        if (current === "DELIVERED" && nextStatus === "DELIVERED") return m;
        return { ...m, deliveryStatus: nextStatus };
      }),
    );
  }, [setReceiptStatus]);

  useEffect(() => {
    onMessageRef.current = handleIncomingMessage;
  }, [handleIncomingMessage]);

  useEffect(() => {
    onReceiptRef.current = handleReceipt;
  }, [handleReceipt]);

  // STOMP lifecycle
  useEffect(() => {
    if (!currentUserId) return;

    const handle = createMessengerStomp({
      onMessage: (frame) => onMessageRef.current(frame),
      onReceipt: (frame) => onReceiptRef.current(frame),
      onConnect: () => setConnected(true),
      onDisconnect: () => setConnected(false),
      onError: (err) => console.error("Messenger STOMP error", err),
    });
    stompRef.current = handle;
    try {
      handle.activate();
    } catch (e) {
      console.error("Messenger STOMP activate failed", e);
    }

    return () => {
      void handle.deactivate();
      stompRef.current = null;
      setConnected(false);
    };
  }, [currentUserId]);

  // Initial conversation list
  useEffect(() => {
    if (!currentUserId) return;
    void refreshConversations();
  }, [currentUserId, refreshConversations]);

  const openConversation = useCallback(
    async (conversationId: number) => {
      setActiveConversationId(conversationId);
      setMobileShowThread(true);
      setMessagesLoading(true);
      try {
        const result = await getMessageHistory(conversationId);
        if (result.responseIndicator === "success" && result.response) {
          const mapped: UiChatMessage[] = result.response.map((m) => ({
            ...m,
            deliveryStatus:
              m.senderId === currentUserIdRef.current
                ? mergeStatus(
                    receiptStatusByMessageIdRef.current.get(Number(m.messageId)),
                    "SENT",
                  )
                : undefined,
          }));
          setMessages(mapped);
          const lastIncoming = [...mapped]
            .reverse()
            .find((m) => m.senderId !== currentUserIdRef.current);
          markConversationRead(
            conversationId,
            lastIncoming?.messageId ?? mapped[mapped.length - 1]?.messageId,
          );
        } else {
          setMessages([]);
        }
      } catch (e) {
        console.error("Failed to load message history", e);
        setMessages([]);
      } finally {
        setMessagesLoading(false);
      }
    },
    [markConversationRead],
  );

  const startChatWithUser = useCallback(
    async (receiverId: number): Promise<ConversationDto | null> => {
      try {
        const result = await createConversation(receiverId);
        if (result.responseIndicator === "success" && result.response) {
          const conv = result.response;
          setConversations((prev) => {
            if (prev.some((c) => c.id === conv.id)) {
              return sortConversations(
                prev.map((c) => (c.id === conv.id ? { ...c, ...conv } : c)),
              );
            }
            return sortConversations([conv, ...prev]);
          });
          await openConversation(conv.id);
          return conv;
        }
      } catch (e) {
        console.error("Failed to create conversation", e);
      }
      return null;
    },
    [openConversation],
  );

  const sendMessage = useCallback(
    async (content: string) => {
      const trimmed = content.trim();
      const conversationId = activeIdRef.current;
      if (!trimmed || !conversationId) return;

      const clientId = `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const optimistic: UiChatMessage = {
        messageId: -Date.now(),
        clientId,
        conversationId,
        senderId: currentUserIdRef.current,
        receiverId: 0,
        message: trimmed,
        messageType: "TEXT",
        createdAt: new Date().toISOString(),
        deliveryStatus: "PENDING",
        isOptimistic: true,
      };
      setMessages((prev) => [...prev, optimistic]);
      upsertConversationPreview(
        {
          messageId: optimistic.messageId,
          conversationId,
          senderId: currentUserIdRef.current,
          receiverId: 0,
          message: trimmed,
          messageType: "TEXT",
          createdAt: optimistic.createdAt,
        },
        false,
      );

      const stomp = stompRef.current;
      if (stomp?.isConnected()) {
        stomp.publishSend({
          conversationId,
          content: trimmed,
          messageType: "TEXT",
        });
        setMessages((prev) =>
          prev.map((m) =>
            m.clientId === clientId
              ? { ...m, deliveryStatus: "SENT", isOptimistic: false }
              : m,
          ),
        );
        // STOMP does not echo to sender — reconcile ids from history
        window.setTimeout(async () => {
          try {
            const result = await getMessageHistory(conversationId);
            if (
              result.responseIndicator === "success" &&
              result.response &&
              activeIdRef.current === conversationId
            ) {
              const history = result.response;
              setMessages((prev) => {
                const statusByMessageId = new Map<number, UiChatMessage["deliveryStatus"]>();
                prev.forEach((msg) => {
                  statusByMessageId.set(Number(msg.messageId), msg.deliveryStatus);
                });
                return history.map((m) => {
                  const cachedReceiptStatus =
                    receiptStatusByMessageIdRef.current.get(
                      Number(m.messageId),
                    );
                  const currentStatus = statusByMessageId.get(
                    Number(m.messageId),
                  );
                  const baseStatus = (cachedReceiptStatus ??
                    currentStatus) as UiChatMessage["deliveryStatus"];
                  const defaultStatus =
                    m.senderId === currentUserIdRef.current
                      ? ("SENT" as const)
                      : undefined;
                  return {
                    ...m,
                    deliveryStatus: mergeStatus(baseStatus, defaultStatus),
                  };
                });
              });
            }
          } catch {
            /* keep optimistic row */
          }
        }, 400);
        return;
      }

      try {
        const result = await sendMessageRest(conversationId, trimmed);
        if (result.responseIndicator === "success" && result.response) {
          const serverMsg = result.response;
          setMessages((prev) =>
            prev.map((m) =>
              m.clientId === clientId
                ? {
                    ...serverMsg,
                    deliveryStatus: "SENT",
                    isOptimistic: false,
                  }
                : m,
            ),
          );
          upsertConversationPreview(serverMsg, false);
        } else {
          setMessages((prev) => prev.filter((m) => m.clientId !== clientId));
        }
      } catch (e) {
        console.error("Failed to send message", e);
        setMessages((prev) => prev.filter((m) => m.clientId !== clientId));
      }
    },
    [upsertConversationPreview],
  );

  const clearActiveConversation = useCallback(() => {
    setActiveConversationId(null);
    setMessages([]);
    setMobileShowThread(false);
  }, []);

  const activeConversation = useMemo(
    () =>
      conversations.find((c) => c.id === activeConversationId) || null,
    [conversations, activeConversationId],
  );

  const totalUnread = useMemo(
    () => conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0),
    [conversations],
  );

  const value: MessengerContextValue = {
    conversations,
    conversationsLoading,
    activeConversationId,
    activeConversation,
    messages,
    messagesLoading,
    connected,
    totalUnread,
    mobileShowThread,
    setMobileShowThread,
    setDrawerViewState,
    refreshConversations,
    openConversation,
    startChatWithUser,
    sendMessage,
    clearActiveConversation,
  };

  return (
    <MessengerContext.Provider value={value}>
      {children}
    </MessengerContext.Provider>
  );
}

export function useMessenger(): MessengerContextValue {
  const ctx = useContext(MessengerContext);
  if (!ctx) {
    throw new Error("useMessenger must be used within MessengerProvider");
  }
  return ctx;
}
